<!--
Author: Eddy <eddy@alphapod.com>
Contributor: Add your name here if you edit this file
Module: Project Page

Description:
This component displays project details, including a title and a paragraph for each detail. The component
automatically fades in when it is mounted.

How to use it:
<ProjectDetails
  :data="data"
/>
-->
<template>
  <section class="main-container">
    <div class="project-description">
      <h1>{{ obj.title }}</h1>
      <section class="tw-flex tw-flex-col tw-gap-6 sgDesktop:tw-gap-7">
        <div
          v-for="(paragraph, index) in obj.description"
          :key="index"
          class="paragraph"
          v-html="paragraph"
        />
      </section>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { fadeInEffect } from '../../support/helper';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  }
});

onMounted(() => {
  fadeInEffect('.project-description h1');
  fadeInEffect('.project-description p');
});

const process = (data: any) => {
  return {
    title: data.title,
    description: data.details.map((value: any) => value.detail)
  };
};

const obj = computed(() => process(props.data));
</script>

<style scoped>
.main-container {
  display: flex;
  justify-content: center;
  margin-top: 120px;
  margin-bottom: 92px;
}
.project-description {
  max-width: 696px;
  padding: 0 24px;
  font-family: 'Inter', sans-serif;
}

.project-description h1 {
  color: #242424;
  font-family: 'Inter', sans-serif;
  font-size: 64px;
  font-style: normal;
  font-weight: 700;
  line-height: 80px;
  letter-spacing: -3px;
  padding-bottom: 64px;
}

.paragraph,
.project-description p {
  color: #242424;
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
}

.project-description p:not(:last-child) {
  margin-bottom: 32px;
}

.project-description:deep(li)::marker{
  font-weight: 700 !important;
}

.project-description:deep(ol) {
  list-style-type: decimal;
  padding-left: 24px;
}

.project-description:deep(li:not(:last-child)) {
  padding-bottom: 28px;
}

.project-description:deep(a) {
  color: #007AFF;
  text-decoration: underline;
  text-underline-offset: 4px;
}

@media screen and (max-width: 820px) {

  .main-container {
    margin: 64px 0;
  }

  .project-description h1 {
    color: var(--Neutrals-Nero, #242424);
    font-family: 'Inter', sans-serif;
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 48px;
    letter-spacing: -1px;
    padding-bottom: 32px;;
  }

  .paragraph,
  .project-description p {
    color: var(--Neutrals-Nero, #242424);
    font-family: 'Inter', sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: -0.25px;
  }

  .project-description {
    padding: 0 40px;
  }

  .project-description p:not(:last-child) {
    margin-bottom: 24px;
  }

  .project-description:deep(li:not(:last-child)) {
    padding-bottom: 24px;
  }
}
</style>
